// All marketing copy lives here. Marketing edits this file; components never
// hard-code words. Keep claims to what ships today (see `whereWeAre`).

export const SITE = {
  name: "Postern",
  legalName: "Postern",
  tagline: "Expansion revenue, operationalised",
  // Canonical origin for og:image / canonical / JSON-LD. Set REACT_APP_SITE_URL
  // at build time (index.html reads the same variable for the crawler fallback).
  // Until the production domain exists we fall back to the origin the page is
  // actually served from, so unfurls always point at an image that resolves.
  url: (process.env.REACT_APP_SITE_URL || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, ""),
  // Product login. Empty (unset) hides every "Log in" link rather than sending
  // people to a hostname that does not resolve yet.
  appUrl: process.env.REACT_APP_APP_URL || "",
  // Contact channels. ALL optional and EMPTY by default: there is no mailbox,
  // LinkedIn page or X handle yet, so nothing on the site may link to one.
  // Every consumer renders its link only when the value is set; the pilot form
  // is the single contact path until then. Set in .env when they exist.
  email: process.env.REACT_APP_CONTACT_EMAIL || "",
  securityEmail: process.env.REACT_APP_SECURITY_EMAIL || process.env.REACT_APP_CONTACT_EMAIL || "",
  // Public Google Calendar appointment-schedule link for the walkthrough form. Empty
  // by default -- when unset, the form falls back to the free-text "preferred time" field.
  bookingUrl: process.env.REACT_APP_BOOKING_URL || "",
  twitter: process.env.REACT_APP_TWITTER || "",
  linkedin: process.env.REACT_APP_LINKEDIN_URL || "",
  defaultDescription:
    "Postern is the post-sale commercial workspace for B2B SaaS: see what each account hasn't bought, quote it, route it for approval, get it signed from a public link, and hand finance the billing schedule. One record, end to end.",
  ogImage: "/og/og-default.jpg",
  lastUpdated: "2026-10-07",
};

export const NAV = [
  { label: "Product", to: "/#product" },
  { label: "How it works", to: "/#how-it-works" },
  { label: "Pricing", to: "/pricing" },
  { label: "Calculators", to: "/calculators" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/about" },
  { label: "Contact", to: "/contact" },
  // Security and Changelog are deliberately not in the top nav -- both are
  // already linked from the footer (Footer.js), and the top nav was carrying
  // the same two destinations twice.
];

export const CTA = {
  primary: "Start a pilot",
  secondary: "Try the pricing calculator",
  walkthrough: "Book a 30-minute walkthrough",
  // One line of proof under the primary button. Only what is true today.
  // hero buttons: the pilot framed as what it gets you, the calculator as a quick try
  heroPrimary: "Find your hidden revenue",
  heroSecondary: "Price a deal",
  pilotProof: "10-day free pilot · no card needed · see deal value in your first session",
};

export const HERO = {
  eyebrow: "Identify. Quote. Govern. Sign.",
  h1a: "Turn account whitespace into",
  h1b: "signed revenue.",
  sub: "Whitespace → priced order form → approval → signature. One record, not five tools.",
  // short tags pinned to points in the hero animation
  tags: ["Expansion", "Approvals", "Renewals", "Forecast"],
  audience: "Built for revenue teams ready to run expansion and renewals as a real motion, not a side project.",
  proof: [
    { k: "Never miss expansion revenue", v: "Every account shows what it hasn't bought, so reps chase revenue instead of hunting for it" },
    { k: "Skip the approval queue", v: "Already-agreed quotes clear themselves and keep a record; exceptions route themselves" },
    { k: "Protect the renewal", v: "Lapsing coverage gets flagged before it costs you the deal" },
    { k: "Forecast you can trust", v: "ARR updates automatically the moment a deal is signed, not whenever someone remembers to type it in" },
  ],
};

export const WHY_NOW = {
  eyebrow: "Why now",
  h2: "Net revenue retention is the board's number, but nobody owns the workflow that produces it.",
};

export const PROBLEMS = [
  { title: "Account truth is scattered", body: "ARR, renewal dates and whitespace live in a CRM, a spreadsheet and someone's memory — never in one place." },
  { title: "Whitespace stalls before it's priced", body: "Knowing what an account hasn't bought is easy. Turning that into a quote the same day rarely happens." },
  { title: "Approvals get buried in threads", body: "Every exception disappears into a thread. Nobody can reconstruct who approved what, or why, three months later." },
  { title: "The forecast is always stale", body: "By the time leadership sees a number, the deal has moved three stages without anyone updating it." },
];

export const COMPARE_TODAY = [
  { tool: "Sheet", text: "Quote built in a spreadsheet, versioned by filename" },
  { tool: "Chat", text: "Discount approved in a thread nobody can find later" },
  { tool: "Email", text: "Redline mailed to legal, then mailed back" },
  { tool: "E-sign", text: "Contract signed in a separate tool" },
  { tool: "CRM", text: "ARR typed in by hand — if anyone remembers" },
];
export const COMPARE_POSTERN = [
  "AM prices whitespace in CPQ",
  "Deal desk clears it — or sees the exception",
  "Legal reviews only if terms deviate",
  "Customer signs from a public link",
  "ARR updates; finance receives the schedule",
];

export const STAGES = [
  {
    id: "whitespace", n: 1, label: "Spot the whitespace", short: "Coverage",
    title: "Every account, mapped against what it hasn't bought yet.",
    body: "Before anything is priced, the coverage matrix shows what each account already has, what's mid-implementation, and what's still open — with a health flag so a renewal never sneaks up.",
    bullets: ["Coverage per module: Live, Impl or Open, with the open amount shown", "Health flags — Healthy, Watch, At risk — surface renewals before they're a surprise", "Search, filter and sort by AM, region or headroom — the same record the AM and CSM both use"],
    // Real, live component here, not a screenshot -- see StageTour.js.
  },
  {
    id: "quote", n: 2, label: "Price & quote", short: "CPQ",
    title: "The AM prices the deal on the order form itself.",
    body: "Line items, pricing, term, billing frequency and currency all live on the order form, not a spreadsheet. CPQ, the PDF and the signing page all compute from that one record, server-side.",
    bullets: ["Multi-year proration, contract-anniversary uplift", "Per-user and flat SKUs, live FX", "Anything over threshold routes for approval by itself"],
    image: "/site/stage-quote.jpg", w: 1452, h: 744, alt: "Postern CPQ: a three-year order form with two line items, header TCV and payment-schedule total agreeing at $324,000",
  },
  {
    id: "govern", n: 3, label: "Govern", short: "Deal desk",
    title: "Every deal gets a decision record. Only exceptions wait for a human.",
    body: "In-policy deals clear instantly, with a full audit trail. Anything over the discount threshold or the rep's signing authority routes to deal desk — only genuine exceptions reach leadership, in one decision.",
    bullets: ["Per-role signing authority on total contract value", "Rationale required, every decision logged", "Approval is bound to the priced content — edit it and it goes back to draft"],
    image: "/site/stage-govern.jpg", w: 1400, h: 750, alt: "Postern deal desk queue with the reason each deal is waiting",
  },
  {
    id: "legal", n: 4, label: "Legal", short: "Queue",
    title: "Non-standard terms go to legal. Standard ones don't.",
    body: "Legal edits preamble, signatory and business terms inline — approve or return to the AM, no email thread.",
    bullets: ["SLA-tracked, coloured by time left", "Inline term editing", "Approve or return in one click"],
    image: "/site/stage-legal.jpg", w: 1400, h: 622, alt: "Postern legal review queue",
  },
  {
    id: "sign", n: 5, label: "Close", short: "Sign",
    title: "Your customer signs from a link. No account, no separate tool.",
    body: "The approved order form becomes a public page the buyer reviews and signs — no account required. Payment can happen on the same link via Stripe.",
    bullets: ["No login for the customer, ever", "PDF and .docx from the same record", "Optional Stripe checkout"],
    image: "/site/stage-sign.jpg", w: 1400, h: 888, alt: "Postern public signing page",
  },
  {
    id: "won", n: 6, label: "Won", short: "ARR",
    title: "Signature closes the deal. Everything downstream updates itself.",
    body: "Signature moves all of it at once: ARR, whitespace, the CRM writeback, the owner's notification, and the hand-off to billing: the signed order form and schedule, ready to invoice in the tool they already use.",
    bullets: ["ARR and whitespace update on signature", "Finance gets the signed PDF and schedule", "Closed-won written back to the Opportunity or Deal"],
    image: "/site/stage-won.jpg", w: 1452, h: 744, alt: "Postern account page after a signed deal",
  },
];

// 6 real login roles (backend Role literal: am, csm, dealdesk, legal,
// leadership, admin) — feature bullet (what changes) + benefit bullet (why
// it matters), matching the accordion design locked in roles_accordion.html.
export const ROLES = [
  { role: "Account manager", badge: "AM",
    feature: "No digging across tools — opportunity, renewals and headroom, together on one screen",
    benefit: "Quote straight from the account you're looking at — no switching apps" },
  { role: "CSM", badge: "CSM",
    feature: "Unsold modules — highlighted right on the whitespace grid",
    benefit: "Renewal dates stand out — flagged the moment they turn critical" },
  { role: "Deal desk", badge: "DD",
    feature: "Only deals needing human intervention land here — everything else auto-clears",
    benefit: "Every approval creates a permanent record — auditable any time" },
  { role: "Legal", badge: "L",
    feature: "No clutter — only deals with non-standard terms reach legal's queue",
    benefit: "Edit terms inline and approve — no redlines over email" },
  { role: "Leadership", badge: "LD",
    feature: "ARR, pipeline and expansion headroom show together, across the whole portfolio",
    benefit: "The forecast updates the moment a deal moves — never a manual re-entry" },
  { role: "Admin", badge: "A",
    feature: "The catalog, discount policy and team roster — all manageable in one place",
    benefit: "Change the policy once, and every quote and approval follows it automatically" },
];

// Single plan as of 2026-09 — seat-based pricing, every feature included
// (no more Launch/Growth feature split; see backend/orders.py PLANS).
export const PLANS = [
  {
    id: "launch", name: "Launch",
    basePrice: 500, includedSeats: 8, perSeat: 50,
    blurb: "No tiers — every feature, from seat one. $500 covers your first 8 seats; $50 for each one after that.",
    features: [
      "Whitespace tracking & CPQ",
      "One-click renewal order forms",
      "Public e-signature — or close with an uploaded signed copy",
      "Discount approvals & signing authority",
      "Legal review queue",
      "Forecast & quota by rep",
      "AI account intelligence",
      "Installment billing",
    ],
    cta: "Start a pilot", intent: "pilot", // no website orders at launch: every customer starts with the pilot
  },
];

// One dated place for every caveat. Update the date when you change a line.
export const WHERE_WE_ARE = {
  updated: "2026-09-04",
  items: [
    { label: "Onboarding", status: "Today", text: "CSV template import for accounts, users and whitespace. Most teams quote their first deal the same day." },
    { label: "Salesforce / HubSpot", status: "Building", text: "Pull accounts and ARR in, push closed-won back. In the making — it will be available soon. If your CRM is the deciding factor, tell us before you buy." },
    { label: "Support", status: "Today", text: "Hands-on setup with the founders, shared Slack channel for pilot teams." },
  ],
  // Security status lives on the dedicated Security page (with fuller
  // context: compliance status, sub-processor list, disclosure program) —
  // deliberately not duplicated here to avoid the two drifting apart.
};

export const FAQ = [
  { group: "Getting started", items: [
    { q: "How long does setup take?", a: "Import your account list with the CSV template and generate the first order form the same day. No implementation team; an ops person can do it alone." },
    { q: "Does this replace our CRM?", a: "No. Postern owns the order form; your CRM owns the customer. A Salesforce/HubSpot connector — pulling accounts in, pushing closed-won back — is in the making; it will be available soon." },
    { q: "Do our customers need to log in to sign?", a: "No. Order forms are shared as a link. Your customer reviews and signs without creating an account." },
  ]},
  { group: "Security & data", items: [
    { q: "How is our data secured?", a: "Encrypted in transit. Every CRM, billing or AI credential you add is encrypted at the field level with a per-deployment key. Tenant isolation is enforced in the data-access layer, not by convention. We do not have a SOC 2 report yet; we will walk your security team through the architecture before you connect anything. See /security." },
    { q: "Do we need our own AI key?", a: "Yes. Account scoring and Document AI run on your own Anthropic key, added once in Settings. Your key talks directly to Anthropic; you control usage and cost." },
    { q: "What happens to our data if we cancel?", a: "Export your full account, whitespace and order-form history at any time, during or after the subscription. We delete on request." },
  ]},
  { group: "Commercial", items: [
    { q: "What are the contract terms?", a: "One plan, every feature included: $500 a month covers your first 8 seats, then $50 per additional seat, billed monthly." },
    { q: "How does invoicing work?", a: "The moment a deal is signed, your billing team receives the signed order form and the billing schedule — no chasing the AM for terms, no re-keying from a PDF. They raise the invoice in the tool you already use, which stays your system of record: Postern doesn't create invoices or track payments. Optional Stripe checkout is available on the signing link." },
  ]},
];

export const CHANGELOG = [
  { date: "2026-09-04", title: "Approval is now bound to priced content", body: "An approved order form can no longer be repriced while keeping its approval. Editing returns it to draft and withdraws the pending approval; the signing link is revoked on every transition out of approved. The discount that routes a deal is computed against the catalog, never against a client-supplied list price.", tags: ["Governance"] },
  { date: "2026-09-04", title: "One set of numbers, everywhere", body: "The CPQ header, the payment-schedule summary, the PDF and the public signing page render the same server-computed ARR, TCV and schedule. Annual uplift applies on contract anniversaries.", tags: ["CPQ"] },
  { date: "2026-09-03", title: "Portfolio ARR aggregated server-side", body: "Headline ARR no longer depends on a paginated client sum. Order-form lists are scoped before truncation so attainment is exact on tenants with long histories.", tags: ["Performance"] },
  { date: "2026-09-03", title: "Single-winner close and seat reservations", body: "Concurrent signature accepts claim the close atomically — ARR moves once. Seat caps are enforced with an atomic reservation on the tenant.", tags: ["Reliability"] },
  { date: "2026-09-03", title: "Legal review cannot be bypassed from the deal desk queue", body: "Decisions check that the approval is still pending and that the order form is in the state the decision expects.", tags: ["Governance"] },
];

export const SECURITY = {
  intro: "Written for the person who has to approve us. Everything below is verifiable against the architecture walkthrough we give every pilot.",
  sections: [
    { h: "Tenancy", items: [
      "Every tenant's data carries a tenant identifier. All reads and writes go through a scoped data-access layer that injects it; a collection outside the registered set cannot be read unscoped — it fails closed.",
      "Cross-tenant joins ($lookup and equivalents) are rejected in the data layer.",
      "Public routes — signing links, invites, account setup — resolve a single document by an unguessable 128-bit token and then scope by that document's tenant.",
    ]},
    { h: "Authentication & authorisation", items: [
      "Session cookies are httpOnly, SameSite=Lax; JWTs are short-lived and versioned so a password reset or deactivation ends every session.",
      "Seven roles (account manager, CSM, deal desk, legal, leadership, admin, plus a platform administrator). Row-level access by account ownership is enforced in the API, not only in the interface.",
      "Signing authority caps by role on total contract value; approvals are bound to the priced content they approved.",
    ]},
    { h: "Encryption", items: [
      "TLS in transit. Every third-party credential you add (CRM tokens, Stripe keys, AI keys, SMTP passwords) is encrypted at the field level with a per-deployment key that never leaves the server.",
      "Database encryption at rest depends on the hosting environment; we state exactly what is in place for your instance in writing.",
    ]},
    { h: "Operations", items: [
      "Backups with a tested, verified restore path — every drill restores into a scratch database and diffs it against live, collection by collection; retention 14 days by default. Today that runs on a schedule you (or we) set up; nightly-by-default scheduling and archive-level encryption are on the roadmap, not shipped yet.",
      "Every approval, legal decision, status change and signature is written to a per-tenant audit log and is exportable.",
      "Dependencies are scanned in CI; we publish fixes in the changelog.",
    ]},
    { h: "Compliance status", items: [
      "No SOC 2 report yet. We're happy to answer any security questions and walk your security team through the architecture.",
      "Data processing agreement and sub-processor list available on request.",
    ]},
  ],
  disclosure: "Found something? Tell us through the pilot form and put 'security' in the message, or use the security contact if one is listed here. We acknowledge within two working days and credit reporters who want it.",
};

export const ABOUT = {
  why: "Growing revenue shouldn't feel like a scavenger hunt. The money is often already sitting in your accounts, hidden behind spreadsheets, old emails and “who said yes to this?” We find it, keep the whole story in one place, and give your team its time back. We built Postern so you never miss revenue that's sitting right in front of you — five tools, in one. Same customers, more revenue.",
  story: "A postern is a quiet aperture — it brings things into closer, sharper focus, so nothing gets lost in the noise. That is what whitespace looks like inside your own accounts: money that was always there, just never in view.",
  // The "Principles" section (order form is the product / governance you can
  // reconstruct / beside the CRM / say what ships) was removed from the About
  // page -- each point just restated something already said more concretely
  // elsewhere (Product section, Roles/audit-log copy, the FAQ, the "Where we
  // are" box). This also resolves the earlier-pinned question about whether
  // the CRM line needed a pilot-status caveat -- it's gone now, not edited.
};
